// Enhanced logging utility for ConsultSRE
// Provides structured logging with component categorization and consistent formatting
import process from 'process';

/* eslint-disable no-undef, no-unused-vars, @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any, no-console */
// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined' && typeof window.document !== 'undefined';
const isDevelopment = process.env.NODE_ENV === 'development';

// Define the logger interface
interface LoggerInterface {
  error: (message: string, metadata?: Record<string, any>) => void;
  warn: (message: string, metadata?: Record<string, any>) => void;
  info: (message: string, metadata?: Record<string, any>) => void;
  debug: (message: string, metadata?: Record<string, any>) => void;
  db: (message: string, metadata?: Record<string, any>) => void;
  api: (message: string, metadata?: Record<string, any>) => void;
  security: (message: string, metadata?: Record<string, any>) => void;
  performance: (message: string, durationMs: number, metadata?: Record<string, any>) => void;
  apiCall: (action: string, metadata?: Record<string, any>) => void;
  userAction: (action: string, metadata?: Record<string, any>) => void;
  getLogs: () => Array<{
    timestamp: string;
    level: string;
    message: string;
    metadata: Record<string, any>;
  }>;
  clearLogs: () => void;
}

// Create a console logger that works in all environments
const createConsoleLogger = (): LoggerInterface => {
  // In-memory log storage with limited capacity
  const inMemoryLogs: Array<{
    timestamp: string;
    level: string;
    message: string;
    metadata: Record<string, any>;
  }> = [];
  const MAX_MEMORY_LOGS = 1000;
  
  const formatMetadata = (metadata: Record<string, any>) => {
    return Object.keys(metadata).length ? ` ${JSON.stringify(metadata)}` : '';
  };

  const logToConsole = (level: string, message: string, metadata: Record<string, any> = {}) => {
    const timestamp = new Date().toISOString();
    const formattedMetadata = formatMetadata(metadata);
    const logMessage = `${timestamp} [${level}]: ${message}${formattedMetadata}`;
    
    // Only log to console in development mode
    if (isDevelopment || !isBrowser) {
      switch(level.toLowerCase()) {
        case 'error':
          console.error(logMessage);
          break;
        case 'warn':
          console.warn(logMessage);
          break;
        case 'debug':
          console.debug(logMessage);
          break;
        default:
          console.log(logMessage);
      }
    }
    
    // Store in memory (only in development)
    if (isDevelopment) {
      inMemoryLogs.push({
        timestamp,
        level,
        message,
        metadata
      });
      
      // Remove oldest log if we exceed the maximum
      if (inMemoryLogs.length > MAX_MEMORY_LOGS) {
        inMemoryLogs.shift();
      }
    }
  };
  
  return {
    error: (message, metadata = {}) => {
      logToConsole('ERROR', message, { ...metadata, timestamp: new Date().toISOString() });
    },
    
    warn: (message, metadata = {}) => {
      logToConsole('WARN', message, { ...metadata, timestamp: new Date().toISOString() });
    },
    
    info: (message, metadata = {}) => {
      logToConsole('INFO', message, { ...metadata, timestamp: new Date().toISOString() });
    },
    
    debug: (message, metadata = {}) => {
      logToConsole('DEBUG', message, { ...metadata, timestamp: new Date().toISOString() });
    },
    
    db: (message, metadata = {}) => {
      logToConsole('INFO', message, { ...metadata, component: 'Database', timestamp: new Date().toISOString() });
    },
    
    api: (message, metadata = {}) => {
      logToConsole('INFO', message, { ...metadata, component: 'API', timestamp: new Date().toISOString() });
    },
    
    security: (message, metadata = {}) => {
      logToConsole('INFO', message, { ...metadata, component: 'Security', timestamp: new Date().toISOString() });
    },
    
    performance: (message, durationMs, metadata = {}) => {
      logToConsole('INFO', message, { 
        ...metadata, 
        component: 'Performance', 
        duration_ms: durationMs,
        timestamp: new Date().toISOString()
      });
    },
    
    apiCall: (action, metadata = {}) => {
      logToConsole('INFO', `API Call: ${action}`, { ...metadata, component: 'APICall', timestamp: new Date().toISOString() });
    },
    
    userAction: (action, metadata = {}) => {
      logToConsole('INFO', `User Action: ${action}`, { ...metadata, component: 'UserAction', timestamp: new Date().toISOString() });
    },
    
    getLogs: () => {
      return isDevelopment ? [...inMemoryLogs] : [];
    },
    
    clearLogs: () => {
      if (isDevelopment) {
        inMemoryLogs.length = 0;
      }
    }
  };
};

// Create a no-op logger for production browser environment
const createNoOpLogger = (): LoggerInterface => ({
  error: () => {},
  warn: () => {},
  info: () => {},
  debug: () => {},
  db: () => {},
  api: () => {},
  security: () => {},
  performance: () => {},
  apiCall: () => {},
  userAction: () => {},
  getLogs: () => [],
  clearLogs: () => {}
});

// Initialize with appropriate logger based on environment
let logger: LoggerInterface = isBrowser && !isDevelopment 
  ? createNoOpLogger()  // Use no-op logger in production browser environment
  : createConsoleLogger(); // Use console logger in development or server environment

// If we're in a Node.js environment, set up the Winston logger asynchronously
if (!isBrowser) {
  // Import Node.js specific modules using dynamic import for ESM compatibility
  const initWinstonLogger = async () => {
    try {
      // Import Winston using dynamic import
      const winstonModule = await import('winston');
      const winston = winstonModule.default;
      
      // Environment configuration
      const LOG_LEVEL = process.env.LOG_LEVEL || (isDevelopment ? 'DEBUG' : 'INFO');
      
      // Update the console format
      const consoleFormat = winston.format.combine(
        winston.format.timestamp(),
        winston.format.colorize(),
        winston.format.printf((info: Record<string, any>) => {
          const { timestamp, level, message, ...metadata } = info;
          const metaStr = Object.keys(metadata).length ? JSON.stringify(metadata) : '';
          return `${timestamp} [${level}]: ${message}${metaStr ? ` ${metaStr}` : ''}`;
        })
      );
      
      // Create a Winston logger that outputs to console (stdout/stderr)
      const winstonLogger = winston.createLogger({
        level: LOG_LEVEL.toLowerCase(),
        levels: {
          error: 0,
          warn: 1,
          info: 2,
          debug: 3
        },
        format: winston.format.combine(
          winston.format.timestamp({ format: 'YYYY-MM-DD HH:mm:ss' }),
          winston.format.json()
        ),
        transports: [
          // Console transport with color
          new winston.transports.Console({
            format: consoleFormat
          })
        ],
        // Handle uncaught exceptions and unhandled rejections
        exceptionHandlers: [
          new winston.transports.Console({
            format: consoleFormat
          })
        ],
        rejectionHandlers: [
          new winston.transports.Console({
            format: consoleFormat
          })
        ],
        exitOnError: false
      });
      
      /**
       * Redacts sensitive information from logs
       * @param {Object} data - Data to redact
       * @returns {Object} Redacted data
       */
      const redactSensitiveInfo = (data: Record<string, any>): Record<string, any> => {
        if (!data || typeof data !== 'object') return data;
        
        const sensitiveKeys = ['password', 'token', 'secret', 'authorization', 'key', 'api_key', 'apiKey', 'credit_card'];
        const result = { ...data };
        
        Object.keys(result).forEach(key => {
          if (sensitiveKeys.some(sk => key.toLowerCase().includes(sk))) {
            result[key] = '[REDACTED]';
          } else if (typeof result[key] === 'object') {
            result[key] = redactSensitiveInfo(result[key]);
          }
        });
        
        return result;
      };
      
      // Create Winston logger implementation
      const winstonLoggerInterface: LoggerInterface = {
        error: (message, metadata = {}) => {
          const sanitizedMetadata = redactSensitiveInfo(metadata);
          winstonLogger.error(message, sanitizedMetadata);
        },
        
        warn: (message, metadata = {}) => {
          const sanitizedMetadata = redactSensitiveInfo(metadata);
          winstonLogger.warn(message, sanitizedMetadata);
        },
        
        info: (message, metadata = {}) => {
          const sanitizedMetadata = redactSensitiveInfo(metadata);
          winstonLogger.info(message, sanitizedMetadata);
        },
        
        debug: (message, metadata = {}) => {
          const sanitizedMetadata = redactSensitiveInfo(metadata);
          winstonLogger.debug(message, sanitizedMetadata);
        },
        
        db: (message, metadata = {}) => {
          const sanitizedMetadata = redactSensitiveInfo({ ...metadata, component: 'Database' });
          winstonLogger.info(message, sanitizedMetadata);
        },
        
        api: (message, metadata = {}) => {
          const sanitizedMetadata = redactSensitiveInfo({ ...metadata, component: 'API' });
          winstonLogger.info(message, sanitizedMetadata);
        },
        
        security: (message, metadata = {}) => {
          const sanitizedMetadata = redactSensitiveInfo({ ...metadata, component: 'Security' });
          winstonLogger.info(message, sanitizedMetadata);
        },
        
        performance: (message, durationMs, metadata = {}) => {
          const sanitizedMetadata = redactSensitiveInfo({ 
            ...metadata, 
            component: 'Performance', 
            duration_ms: durationMs 
          });
          winstonLogger.info(message, sanitizedMetadata);
        },
        
        apiCall: (action, metadata = {}) => {
          const sanitizedMetadata = redactSensitiveInfo({ ...metadata, component: 'APICall' });
          winstonLogger.info(`API Call: ${action}`, sanitizedMetadata);
        },
        
        userAction: (action, metadata = {}) => {
          const sanitizedMetadata = redactSensitiveInfo({ ...metadata, component: 'UserAction' });
          winstonLogger.info(`User Action: ${action}`, sanitizedMetadata);
        },
        
        getLogs: () => [], // In-memory logs not supported with Winston
        
        clearLogs: () => {} // No-op for Winston
      };
      
      // Replace the default logger with Winston logger
      logger = winstonLoggerInterface;
      
    } catch (error) {
      // If Winston fails to load, fall back to console logger
      console.error('Failed to initialize Winston logger, falling back to console logger');
      console.error(error);
    }
  };
  
  // Initialize Winston logger
  initWinstonLogger().catch(err => {
    console.error('Error initializing Winston logger:', err);
  });
}

export default logger;
/* eslint-enable no-undef, no-unused-vars, @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any, no-console */ 